import { useState, useEffect } from 'react';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Settings, 
  TestTube, 
  CheckCircle, 
  AlertCircle,
  Save,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';
import { alertingApi } from '../utils/api';
import { TestAlertResult } from '../types';

const AlertingConfig: React.FC = () => {
  const [alertingStatus, setAlertingStatus] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestAlertResult | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testingDiscord, setTestingDiscord] = useState(false);
  const [testDiscordResult, setTestDiscordResult] = useState<string | null>(null);
  const [testingSlack, setTestingSlack] = useState(false);
  const [testSlackResult, setTestSlackResult] = useState<string | null>(null);

  // Email configuration form
  const [emailConfig, setEmailConfig] = useState({
    host: '',
    port: 587,
    user: '',
    pass: '',
    from: '',
    alert_email: ''
  });

  // Discord configuration form
  const [discordConfig, setDiscordConfig] = useState({
    webhook_url: ''
  });

  // Slack configuration form
  const [slackConfig, setSlackConfig] = useState({
    webhook_url: ''
  });

  // Threshold configuration
  const [thresholds, setThresholds] = useState({
    down_threshold: 1,
    up_threshold: 1
  });

  // Alert frequency configuration
  const [alertFrequency, setAlertFrequency] = useState<'always' | 'on_change'>('always');
  const [alertDebounce, setAlertDebounce] = useState<number>(1);

  // Sectioned save states
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingDiscord, setSavingDiscord] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [savingSlack, setSavingSlack] = useState(false);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [successDiscord, setSuccessDiscord] = useState<string | null>(null);
  const [successThresholds, setSuccessThresholds] = useState<string | null>(null);
  const [successSlack, setSuccessSlack] = useState<string | null>(null);
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [errorDiscord, setErrorDiscord] = useState<string | null>(null);
  const [errorThresholds, setErrorThresholds] = useState<string | null>(null);
  const [errorSlack, setErrorSlack] = useState<string | null>(null);

  useEffect(() => {
    loadAlertingStatus();
  }, []);

  const loadAlertingStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await alertingApi.getAlertingStatus();
      setAlertingStatus(status);
      // Pre-fill form with existing configuration (except password)
      if (status.alerting?.email) {
        setEmailConfig((prev) => ({
          ...prev,
          host: status.alerting.email.smtp_host ?? '',
          port: status.alerting.email.smtp_port ?? 587,
          user: status.alerting.email.smtp_user ?? '',
          from: status.alerting.email.smtp_from ?? '',
          alert_email: status.alerting.email.alert_email ?? '',
          pass: '', // always blank for security
        }));
      }
      if (status.alerting?.discord) {
        setDiscordConfig({
          webhook_url: status.alerting.discord.webhook_url ?? ''
        });
      }
      if (status.alerting?.slack) {
        setSlackConfig({
          webhook_url: status.alerting.slack.webhook_url ?? ''
        });
      }
      if (status.alerting?.thresholds) {
        setThresholds({
          down_threshold: status.alerting.thresholds.down_threshold ?? 1,
          up_threshold: status.alerting.thresholds.up_threshold ?? 1
        });
      } else {
        setThresholds({ down_threshold: 1, up_threshold: 1 });
      }
      
      // Load alert frequency
      if (status.alerting?.alert_frequency) {
        setAlertFrequency(status.alerting.alert_frequency);
      } else {
        setAlertFrequency('always');
      }
      if (status.alerting?.alert_debounce) {
        setAlertDebounce(status.alerting.alert_debounce);
      } else {
        setAlertDebounce(1);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerting status');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Build config object with only changed fields
      const configToUpdate: any = {};
      if (emailConfig.host) configToUpdate.smtp_host = emailConfig.host;
      if (emailConfig.port) configToUpdate.smtp_port = Number(emailConfig.port);
      if (emailConfig.user) configToUpdate.smtp_user = emailConfig.user;
      if (emailConfig.pass) configToUpdate.smtp_pass = emailConfig.pass;
      if (emailConfig.from) configToUpdate.smtp_from = emailConfig.from;
      if (emailConfig.alert_email) configToUpdate.alert_email = emailConfig.alert_email;
      if (discordConfig.webhook_url) configToUpdate.discord_webhook_url = discordConfig.webhook_url;
      if (thresholds.down_threshold) configToUpdate.alert_down_threshold = thresholds.down_threshold;
      if (thresholds.up_threshold) configToUpdate.alert_up_threshold = thresholds.up_threshold;
      // Always include alert frequency settings
      configToUpdate.alert_frequency = alertFrequency;
      configToUpdate.alert_debounce = alertDebounce;

      await alertingApi.updateConfig(configToUpdate);
      setSuccess('Alerting configuration and frequency settings saved successfully!');
      setEmailConfig((prev) => ({ ...prev, pass: '' })); // Clear password field after save
      await loadAlertingStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAlerts = async () => {
    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      
      const result = await alertingApi.sendTestAlerts();
      setTestResult(result);
      
      if (result.email && result.discord && result.slack) {
        setSuccess('Test alerts sent successfully!');
      } else if (result.email || result.discord || result.slack) {
        setSuccess(`Test alerts sent: ${result.message}`);
      } else {
        setError('Failed to send test alerts');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test alerts');
    } finally {
      setTesting(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      
      const result = await alertingApi.sendTestEmail();
      
      if (result.emailSent) {
        setSuccess('Test email sent successfully!');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleTestDiscord = async () => {
    try {
      setTestingDiscord(true);
      setTestDiscordResult(null);
      const result = await alertingApi.sendTestDiscord();
      setTestDiscordResult(result.message);
    } catch (err) {
      setTestDiscordResult('Failed to send test Discord alert');
    } finally {
      setTestingDiscord(false);
    }
  };

  const handleTestSlack = async () => {
    try {
      setTestingSlack(true);
      setTestSlackResult(null);
      const result = await alertingApi.sendTestSlack();
      setTestSlackResult(result.message);
    } catch (err) {
      setTestSlackResult('Failed to send test Slack alert');
    } finally {
      setTestingSlack(false);
    }
  };

  // Sectioned save handlers
  const handleSaveEmail = async () => {
    try {
      setSavingEmail(true);
      setErrorEmail(null);
      setSuccessEmail(null);
      const configToUpdate: any = {};
      if (emailConfig.host) configToUpdate.smtp_host = emailConfig.host;
      if (emailConfig.port) configToUpdate.smtp_port = Number(emailConfig.port);
      if (emailConfig.user) configToUpdate.smtp_user = emailConfig.user;
      if (emailConfig.pass) configToUpdate.smtp_pass = emailConfig.pass;
      if (emailConfig.from) configToUpdate.smtp_from = emailConfig.from;
      if (emailConfig.alert_email) configToUpdate.alert_email = emailConfig.alert_email;
      // Only send fields that are non-empty (not blank or undefined)
      Object.keys(configToUpdate).forEach(key => {
        if (configToUpdate[key] === '' || configToUpdate[key] === undefined) {
          delete configToUpdate[key];
        }
      });
      await alertingApi.updateConfig(configToUpdate);
      setSuccessEmail('Email configuration saved!');
      setEmailConfig((prev) => ({ ...prev, pass: '' }));
      await loadAlertingStatus();
    } catch (err) {
      setErrorEmail(err instanceof Error ? err.message : 'Failed to save email config');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveDiscord = async () => {
    try {
      setSavingDiscord(true);
      setErrorDiscord(null);
      setSuccessDiscord(null);
      const configToUpdate: any = {};
      if (discordConfig.webhook_url) configToUpdate.discord_webhook_url = discordConfig.webhook_url;
      // Only send if not blank
      if (configToUpdate.discord_webhook_url === '' || configToUpdate.discord_webhook_url === undefined) {
        delete configToUpdate.discord_webhook_url;
      }
      await alertingApi.updateConfig(configToUpdate);
      setSuccessDiscord('Discord configuration saved!');
      await loadAlertingStatus();
    } catch (err) {
      setErrorDiscord(err instanceof Error ? err.message : 'Failed to save Discord config');
    } finally {
      setSavingDiscord(false);
    }
  };

  const handleSaveSlack = async () => {
    try {
      setSavingSlack(true);
      setErrorSlack(null);
      setSuccessSlack(null);
      const configToUpdate: any = { slack_webhook_url: slackConfig.webhook_url || null };
      await alertingApi.updateConfig(configToUpdate);
      setSuccessSlack('Slack configuration saved!');
      await loadAlertingStatus();
    } catch (err) {
      setErrorSlack(err instanceof Error ? err.message : 'Failed to save Slack config');
    } finally {
      setSavingSlack(false);
    }
  };

  const handleSaveThresholds = async () => {
    try {
      setSavingThresholds(true);
      setErrorThresholds(null);
      setSuccessThresholds(null);
      const configToUpdate: any = {};
      if (thresholds.down_threshold) configToUpdate.alert_down_threshold = thresholds.down_threshold;
      if (thresholds.up_threshold) configToUpdate.alert_up_threshold = thresholds.up_threshold;
      configToUpdate.alert_frequency = alertFrequency;
      configToUpdate.alert_debounce = alertDebounce;
      await alertingApi.updateConfig(configToUpdate);
      setSuccessThresholds('Thresholds and alert frequency saved!');
      await loadAlertingStatus();
    } catch (err) {
      setErrorThresholds(err instanceof Error ? err.message : 'Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading alerting configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-6 h-6 text-primary" />
          <h2 className="text-2xl font-bold">Alerting Configuration</h2>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={loadAlertingStatus}
            className="btn btn-outline btn-sm"
            disabled={loading}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          
          <button
            onClick={handleSaveConfig}
            className="btn btn-primary btn-sm"
            disabled={saving}
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save All Settings
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Current Status */}
      {alertingStatus && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-primary">
              <Mail className="w-8 h-8" />
            </div>
            <div className="stat-title">Email Alerts</div>
            <div className={`stat-value ${alertingStatus.summary?.emailConfigured ? 'text-success' : 'text-error'}`}>
              {alertingStatus.summary?.emailConfigured ? 'Configured' : 'Not Configured'}
            </div>
            <div className="stat-desc text-xs mt-2">
              <strong>Host:</strong> {alertingStatus.alerting?.email?.smtp_host ?? '—'}<br />
              <strong>User:</strong> {alertingStatus.alerting?.email?.smtp_user ?? '—'}<br />
              <strong>From:</strong> {alertingStatus.alerting?.email?.smtp_from ?? '—'}<br />
              <strong>Alert Email:</strong> {alertingStatus.alerting?.email?.alert_email ?? '—'}
            </div>
          </div>
          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-primary">
              <MessageSquare className="w-8 h-8" />
            </div>
            <div className="stat-title">Discord Alerts</div>
            <div className={`stat-value ${alertingStatus.summary?.discordConfigured ? 'text-success' : 'text-error'}`}>
              {alertingStatus.summary?.discordConfigured ? 'Configured' : 'Not Configured'}
            </div>
            <div className="stat-desc text-xs mt-2">
              <strong>Webhook:</strong> {alertingStatus.alerting?.discord?.webhook_url ?? '—'}
            </div>
          </div>
          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-primary">
              <MessageSquare className="w-8 h-8" />
            </div>
            <div className="stat-title">Slack Alerts</div>
            <div className={`stat-value ${alertingStatus.alerting?.slack?.configured ? 'text-success' : 'text-error'}`}>
              {alertingStatus.alerting?.slack?.configured ? 'Configured' : 'Not Configured'}
            </div>
            <div className="stat-desc text-xs mt-2">
              <strong>Webhook:</strong> {alertingStatus.alerting?.slack?.webhook_url ? '••••••••' : '—'}
            </div>
          </div>
          <div className="stat bg-base-100 shadow-lg rounded-lg">
            <div className="stat-figure text-primary">
              <Settings className="w-8 h-8" />
            </div>
            <div className="stat-title">Alert Thresholds</div>
            <div className={`stat-value ${alertingStatus.summary?.thresholdsConfigured ? 'text-success' : 'text-error'}`}>
              {alertingStatus.summary?.thresholdsConfigured ? 'Configured' : 'Not Configured'}
            </div>
            <div className="stat-desc text-xs mt-2">
              <strong>Down:</strong> {alertingStatus.alerting?.thresholds?.down_threshold ?? '—'}<br />
              <strong>Up:</strong> {alertingStatus.alerting?.thresholds?.up_threshold ?? '—'}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email Configuration */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-6 h-6 text-blue-500" />
              <h3 className="card-title">Email Configuration</h3>
            </div>
            {errorEmail && (
              <div className="alert alert-error"><span>{errorEmail}</span></div>
            )}
            {successEmail && (
              <div className="alert alert-success"><span>{successEmail}</span></div>
            )}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">SMTP Host</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered"
                    value={emailConfig.host}
                    onChange={(e) => setEmailConfig({ ...emailConfig, host: e.target.value })}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">SMTP Port</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered"
                    value={emailConfig.port}
                    onChange={(e) => setEmailConfig({ ...emailConfig, port: parseInt(e.target.value) })}
                    placeholder="587"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Username</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered"
                    value={emailConfig.user}
                    onChange={(e) => setEmailConfig({ ...emailConfig, user: e.target.value })}
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Password</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input input-bordered pr-10"
                      value={emailConfig.pass}
                      onChange={(e) => setEmailConfig({ ...emailConfig, pass: e.target.value })}
                      placeholder="App password"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">From Email</span>
                  </label>
                  <input
                    type="email"
                    className="input input-bordered"
                    value={emailConfig.from}
                    onChange={(e) => setEmailConfig({ ...emailConfig, from: e.target.value })}
                    placeholder="alerts@yourdomain.com"
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Alert Email</span>
                  </label>
                  <input
                    type="email"
                    className="input input-bordered"
                    value={emailConfig.alert_email}
                    onChange={(e) => setEmailConfig({ ...emailConfig, alert_email: e.target.value })}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleSaveEmail}
                  className="btn btn-primary btn-sm"
                  disabled={savingEmail}
                >
                  {savingEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Email
                </button>
                <button
                  onClick={handleTestEmail}
                  className="btn btn-outline btn-sm"
                  disabled={testing}
                >
                  {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                  Test Email
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Discord Configuration */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-6 h-6 text-purple-500" />
              <h3 className="card-title">Discord Configuration</h3>
            </div>
            {errorDiscord && (
              <div className="alert alert-error"><span>{errorDiscord}</span></div>
            )}
            {successDiscord && (
              <div className="alert alert-success"><span>{successDiscord}</span></div>
            )}
            {testDiscordResult && (
              <div className="alert alert-info"><span>{testDiscordResult}</span></div>
            )}
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Webhook URL</span>
                </label>
                <input
                  type="url"
                  className="input input-bordered"
                  value={discordConfig.webhook_url}
                  onChange={(e) => setDiscordConfig({ webhook_url: e.target.value })}
                  placeholder="https://discord.com/api/webhooks/..."
                />
                <label className="label">
                  <span className="label-text-alt">
                    Create a webhook in your Discord server settings
                  </span>
                </label>
              </div>
              <div className="alert alert-info">
                <MessageSquare className="w-4 h-4" />
                <span>
                  Discord webhooks will send notifications to your configured channel
                </span>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleSaveDiscord}
                  className="btn btn-primary btn-sm"
                  disabled={savingDiscord}
                >
                  {savingDiscord ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Discord
                </button>
                <button
                  onClick={handleTestDiscord}
                  className="btn btn-outline btn-sm"
                  disabled={testingDiscord}
                >
                  {testingDiscord ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                  Test Discord
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Slack Configuration */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-6 h-6 text-green-500" />
              <h3 className="card-title">Slack Configuration</h3>
            </div>
            {errorSlack && (
              <div className="alert alert-error"><span>{errorSlack}</span></div>
            )}
            {successSlack && (
              <div className="alert alert-success"><span>{successSlack}</span></div>
            )}
            {testSlackResult && (
              <div className="alert alert-info"><span>{testSlackResult}</span></div>
            )}
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Incoming Webhook URL</span>
                </label>
                <input
                  type="url"
                  className="input input-bordered"
                  value={slackConfig.webhook_url}
                  onChange={(e) => setSlackConfig({ webhook_url: e.target.value })}
                  placeholder="https://hooks.slack.com/services/..."
                />
                <label className="label">
                  <span className="label-text-alt">
                    Create an incoming webhook in your Slack app settings
                  </span>
                </label>
              </div>
              <div className="alert alert-info">
                <MessageSquare className="w-4 h-4" />
                <span>
                  Slack webhooks post rich alert messages to your chosen channel
                </span>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={handleSaveSlack}
                  className="btn btn-primary btn-sm"
                  disabled={savingSlack}
                >
                  {savingSlack ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Slack
                </button>
                <button
                  onClick={handleTestSlack}
                  className="btn btn-outline btn-sm"
                  disabled={testingSlack}
                >
                  {testingSlack ? <RefreshCw className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                  Test Slack
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Alert Thresholds */}
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-6 h-6 text-orange-500" />
            <h3 className="card-title">Alert Thresholds</h3>
          </div>
          {errorThresholds && (
            <div className="alert alert-error"><span>{errorThresholds}</span></div>
          )}
          {successThresholds && (
            <div className="alert alert-success"><span>{successThresholds}</span></div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold">Down Alert Threshold</h4>
              <p className="text-sm opacity-70">
                Number of consecutive failed pings before sending a down alert
              </p>
              <div className="form-control">
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="input input-bordered w-32"
                  value={thresholds.down_threshold}
                  onChange={(e) => setThresholds({ ...thresholds, down_threshold: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-semibold">Recovery Alert Threshold</h4>
              <p className="text-sm opacity-70">
                Number of consecutive successful pings before sending a recovery alert
              </p>
              <div className="form-control">
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="input input-bordered w-32"
                  value={thresholds.up_threshold}
                  onChange={(e) => setThresholds({ ...thresholds, up_threshold: parseInt(e.target.value) })}
                />
              </div>
            </div>
          </div>
          
          <div className="divider"></div>
          
          <div className="space-y-4">
            <h4 className="font-semibold">Alert Frequency</h4>
            <p className="text-sm opacity-70">
              Choose when alerts should be sent
            </p>
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="radio"
                  name="alertFrequency"
                  value="always"
                  checked={alertFrequency === 'always'}
                  onChange={(e) => setAlertFrequency(e.target.value as 'always' | 'on_change')}
                  className="radio radio-primary"
                />
                <span className="label-text">
                  <strong>Always</strong> - Send alerts for every ping that meets the threshold
                </span>
              </label>
            </div>
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="radio"
                  name="alertFrequency"
                  value="on_change"
                  checked={alertFrequency === 'on_change'}
                  onChange={(e) => setAlertFrequency(e.target.value as 'always' | 'on_change')}
                  className="radio radio-primary"
                />
                <span className="label-text">
                  <strong>On Change Only</strong> - Send alerts only when status changes (down → up or up → down)
                </span>
              </label>
            </div>
            
            {alertFrequency === 'always' && (
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Alert Debounce</span>
                  <span className="label-text-alt">Send alert every X pings (e.g., 5 = send alert every 5th ping)</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className="input input-bordered"
                  value={alertDebounce}
                  onChange={(e) => setAlertDebounce(parseInt(e.target.value) || 1)}
                  placeholder="1"
                />
              </div>
            )}
          </div>
          <button
            onClick={handleSaveThresholds}
            className="btn btn-primary btn-sm mt-4"
            disabled={savingThresholds}
          >
            {savingThresholds ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Thresholds
          </button>
        </div>
      </div>

      {/* Test Results */}
      {testResult && (
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex items-center gap-2 mb-4">
              <TestTube className="w-6 h-6 text-green-500" />
              <h3 className="card-title">Test Results</h3>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>Email: {testResult.email ? '✅ Sent' : '❌ Failed'}</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                <span>Discord: {testResult.discord ? '✅ Sent' : '❌ Failed'}</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                <span>Slack: {testResult.slack ? '✅ Sent' : '❌ Failed'}</span>
              </div>
              <p className="text-sm opacity-70 mt-2">{testResult.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Test All Alerts Button */}
      <div className="flex justify-center">
        <button
          onClick={handleTestAlerts}
          className="btn btn-primary btn-lg"
          disabled={testing}
        >
          {testing ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <TestTube className="w-5 h-5" />
          )}
          Test All Alerts
        </button>
      </div>
    </div>
  );
};

export default AlertingConfig; 